import { z } from 'zod';

import { ANCHOR_CHAIN_IDS } from '@/features/wallet/config/anchors';
import type { AnchorChainId } from '@/features/wallet/config/anchors';
import {
    CRONOS_CUSTODIAL_WALLET_MANAGER,
    custodialManagerFor,
} from '@/lib/config/custodialManagers';

// re-exported for the existing consumers that import it from this module
export { CRONOS_CUSTODIAL_WALLET_MANAGER };

/**
 * The anchor a statement is scoped to. It is an unsigned wire HINT everywhere it
 * travels — what actually binds the anchor is `domain.chainId` plus the
 * `verifyingContract` inside the signature, which is why the domain schema below
 * insists the two agree.
 */
export const anchorChainIdSchema = z.union([
    z.literal(ANCHOR_CHAIN_IDS[0]),
    z.literal(ANCHOR_CHAIN_IDS[1]),
]);

/**
 * `verifyingContract` must be exactly the manager THIS app has pinned for the
 * domain's own chain. A response that named any other contract would have the
 * user authorize a statement against something the server chose.
 */
export function assertAnchorVerifyingContract(
    chainId: AnchorChainId,
    verifyingContract: string,
): boolean {
    try {
        return (
            verifyingContract.toLowerCase() ===
            custodialManagerFor(chainId).toLowerCase()
        );
    } catch {
        // no manager deployed on that anchor yet: nothing may be signed for it.
        return false;
    }
}

export const tradingSessionMethodSchema = z.enum([
    'oauth',
    'eip712',
    'passkey',
]);
export const tradingSessionDurationMinutesSchema = z.union([
    z.literal(5),
    z.literal(15),
    z.literal(30),
    z.literal(60),
]);
export const tradingSessionWindowSecondsSchema = z.union([
    z.literal(300),
    z.literal(900),
    z.literal(1800),
    z.literal(3600),
]);

export const bytes32HexSchema = z.string().regex(/^0x[0-9a-fA-F]{64}$/u);
export const eoaAddressSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/u);

export const tradingSessionStatementSchema = z
    .object({
        protocol: z.literal('snappy-trading-session'),
        version: z.literal(1),
        sessionId: bytes32HexSchema,
        owner: eoaAddressSchema,
        authorizationWallet: eoaAddressSchema,
        tradingWallet: eoaAddressSchema,
        audience: z.literal('api.wolfswap.app'),
        origin: z.string().min(1),
        issuedAt: z.number().int().nonnegative(),
        expiresAt: z.number().int().positive(),
    })
    .strict();

function exactTypedDataFieldSchema<
    const Name extends string,
    const Type extends string,
>(name: Name, type: Type) {
    return z.object({ name: z.literal(name), type: z.literal(type) }).strict();
}

export const tradingSessionTypedDataDomainSchema = z
    .object({
        name: z.literal('SnappyTradingSession'),
        version: z.literal('1'),
        chainId: anchorChainIdSchema,
        verifyingContract: eoaAddressSchema,
    })
    .strict()
    .superRefine((domain, context) => {
        if (
            !assertAnchorVerifyingContract(
                domain.chainId,
                domain.verifyingContract,
            )
        ) {
            context.addIssue({
                code: 'custom',
                path: ['verifyingContract'],
                message: 'Unexpected EIP-712 verifying contract',
            });
        }
    });

export const tradingSessionTypedDataSchema = z
    .object({
        domain: tradingSessionTypedDataDomainSchema,
        types: z
            .object({
                TradingSession: z.tuple([
                    exactTypedDataFieldSchema('sessionId', 'bytes32'),
                    exactTypedDataFieldSchema('owner', 'address'),
                    exactTypedDataFieldSchema('authorizationWallet', 'address'),
                    exactTypedDataFieldSchema('tradingWallet', 'address'),
                    exactTypedDataFieldSchema('audienceHash', 'bytes32'),
                    exactTypedDataFieldSchema('originHash', 'bytes32'),
                    exactTypedDataFieldSchema('issuedAt', 'uint64'),
                    exactTypedDataFieldSchema('expiresAt', 'uint64'),
                ]),
            })
            .strict(),
        primaryType: z.literal('TradingSession'),
        message: z
            .object({
                sessionId: bytes32HexSchema,
                owner: eoaAddressSchema,
                authorizationWallet: eoaAddressSchema,
                tradingWallet: eoaAddressSchema,
                audienceHash: bytes32HexSchema,
                originHash: bytes32HexSchema,
                issuedAt: z.number().int().nonnegative(),
                expiresAt: z.number().int().positive(),
            })
            .strict(),
    })
    .strict();

export const tradingSessionChallengeResponseSchema = z
    .object({
        statement: tradingSessionStatementSchema,
        challenge: z.string().min(1),
        eip712: tradingSessionTypedDataSchema,
        // echoed by an anchor-aware backend; absent from an older one, which
        // can only ever have meant Cronos.
        anchorChainId: anchorChainIdSchema.optional(),
    })
    .strict();

export const publicKeyRequestOptionsSchema = z
    .object({
        challenge: z.string().min(1),
        rpId: z.string().optional(),
        timeout: z.number().positive().optional(),
        userVerification: z.literal('required'),
        allowCredentials: z
            .array(
                z
                    .object({
                        id: z.string().min(1),
                        type: z.literal('public-key'),
                        transports: z
                            .array(
                                z.enum([
                                    'ble',
                                    'cable',
                                    'hybrid',
                                    'internal',
                                    'nfc',
                                    'smart-card',
                                    'usb',
                                ]),
                            )
                            .optional(),
                    })
                    .strict(),
            )
            .optional(),
    })
    .strict();

export const canonicalPasskeyRequestOptionsSchema =
    publicKeyRequestOptionsSchema.extend({
        rpId: z.string().min(1),
    });

export const passkeyTradingSessionChallengeResponseSchema =
    tradingSessionChallengeResponseSchema.extend({
        publicKey: canonicalPasskeyRequestOptionsSchema,
    });

export const passkeyDiscoveryOptionsResponseSchema = z
    .object({
        publicKey: z
            .object({
                challenge: z.string().min(1),
                rpId: z.string().min(1),
                userVerification: z.literal('required'),
            })
            .strict(),
    })
    .strict();

export const passkeyOwnerDiscoveryResponseSchema = z
    .object({ owner: eoaAddressSchema })
    .strict();

export const tradingSessionStatusSchema = z.discriminatedUnion('state', [
    z
        .object({
            state: z.literal('active'),
            method: tradingSessionMethodSchema,
            owner: eoaAddressSchema.optional(),
            sessionId: bytes32HexSchema.optional(),
            // which anchor the window is open on; absent on oauth-only sessions
            anchorChainId: anchorChainIdSchema.optional(),
            issuedAtMs: z.number().int().nonnegative(),
            expiresAtMs: z.number().int().positive(),
            serverTimeMs: z.number().int().nonnegative(),
        })
        .strict()
        .superRefine((value, context) => {
            if (
                (value.owner === undefined) !==
                (value.sessionId === undefined)
            ) {
                context.addIssue({
                    code: 'custom',
                    message:
                        'Trading-session correlation requires both owner and sessionId.',
                });
            }
        }),
    z
        .object({
            state: z.literal('none'),
            method: z.null(),
            issuedAtMs: z.null(),
            expiresAtMs: z.null(),
            serverTimeMs: z.number().int().nonnegative(),
        })
        .strict(),
]);

/**
 * `GET /v2/trading-session/probe` always answers 200 and always re-sets the
 * short-lived `__Secure-sntsp` cookie, so two consecutive calls reveal whether
 * the browser keeps our cross-site cookies at all.
 */
export const tradingSessionCookieProbeResponseSchema = z
    .object({
        cookieReceived: z.boolean(),
        serverTimeMs: z.number().int().nonnegative(),
    })
    .strict();

export const tradingSessionFinishResponseSchema = z
    .object({
        method: z.enum(['eip712', 'passkey']),
        owner: eoaAddressSchema,
        expiresAt: z.number().int().positive(),
        anchorChainId: anchorChainIdSchema.optional(),
    })
    .strict();

export const accessTokenResponseSchema = z
    .object({
        token: z.string().min(1),
        expiresAtMs: z.number().int().positive(),
        owner: eoaAddressSchema.optional(),
        sessionId: bytes32HexSchema.optional(),
    })
    .strict()
    .superRefine((value, context) => {
        if ((value.owner === undefined) !== (value.sessionId === undefined)) {
            context.addIssue({
                code: 'custom',
                message:
                    'Access-token correlation requires both owner and sessionId.',
            });
        }
    });

export const logoutResponseSchema = z.object({ ok: z.literal(true) }).strict();
